import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      if (typeof window !== 'undefined') {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
};

export const userAPI = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  updateRole: (userId, role) => api.patch(`/users/${userId}/role`, { role }),
  delete: (userId) => api.delete(`/users/${userId}`),
};

export const employeeAPI = {
  getAll: () => api.get('/employees'),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  delete: (id) => api.delete(`/employees/${id}`),
  reorder: (employeeIds) => api.post('/employees/reorder', { employee_ids: employeeIds }),
  getSchedule: (employeeId, startDate, endDate) =>
    api.get(`/employees/${employeeId}/schedule`, {
      params: { start_date: startDate, end_date: endDate },
    }),
  getAvailability: (employeeId, startDate, endDate) =>
    api.get(`/employees/${employeeId}/availability`, {
      params: { start_date: startDate, end_date: endDate },
    }),
  setAvailability: (data) => api.post('/employees/availability', data),
  deleteAvailability: (id) => api.delete(`/employees/availability/${id}`),
};

export const workOrderAPI = {
  getAll: () => api.get('/workorders'),
  getOne: (id) => api.get(`/workorders/${id}`),
  create: (data) => api.post('/workorders', data),
  update: (id, data) => api.put(`/workorders/${id}`, data),
  delete: (id) => api.delete(`/workorders/${id}`),
  sync: () => api.post('/workorders/sync'),
  getSyncStatus: () => api.get('/workorders/sync-status'),
  pushToNetsuite: () => api.post('/workorders/push-to-netsuite'),
};

export const purchaseOrderAPI = {
  getAll: () => api.get('/purchase-orders'),
  sync: () => api.post('/purchase-orders/sync'),
};

export const deleteWorkOrder = async (id) => {
  const response = await api.delete(`/workorders/${id}`);
  return response.data;
};

export const assignmentAPI = {
  getAll: (startDate, endDate) =>
    api.get('/assignments', {
      params: { start_date: startDate, end_date: endDate },
    }),
  create: (data) => api.post('/assignments', data),
  update: (id, data) => api.put(`/assignments/${id}`, data),
  delete: (id) => api.delete(`/assignments/${id}`),
  autoAssign: (workOrderIds) =>
    api.post('/assignments/auto-assign', { work_order_ids: workOrderIds }),
  // Smart assign with automatic splitting across days
  smartAssign: (data) => api.post('/assignments/smart-assign', data),
  // Move all assignments for a work order to new location with splitting
  moveWorkOrder: (data) => api.post('/assignments/move-workorder', data),
  // Unassign all assignments for a work order (Admin only)
  unassignWorkOrder: (workOrderId) => api.delete(`/assignments/workorder/${workOrderId}`),
};

export const capacityAPI = {
  get: (startDate, endDate) =>
    api.get('/capacity', {
      params: { start_date: startDate, end_date: endDate },
    }),
};

export const settingsAPI = {
  getNetSuite: () => api.get('/settings/netsuite'),
  updateNetSuite: (credentials) => api.post('/settings/netsuite', credentials),
};

export default api;
